import { useState } from "react";
import { Download, Printer } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { useToast } from "../../components/Toast";
import { API_BASE, exportUrl } from "../../api/client";
import type { ExportFormat, ExportScope, PdfLayout } from "../../api/types";

// 匯出與列印(業主 2026-09-17)。
//
// > 匯出PDF/匯出EXCEL 合為一個按鈕 / 按下匯出後 給我們勾選要 EXCEL 還是PDF
// > 再來下面還會有這些選項 1. 整張報價單 2. 只列印明細(隱藏價格)
// > 報價單 新增列印功能 列印按下去後 跳出懸浮視窗
//
// ==========================================================================
// 兩顆鈕、一個對話框
// ==========================================================================
// 匯出與列印問的是同一組問題(要哪一份),差別只在拿到之後做什麼:
// 存檔還是送印表機。所以版面共用,只有最後那一顆鈕不一樣。
//
// 列印**只有 PDF** —— Excel 沒有「列印」這回事,要印也是先開 Excel 再印。
//
// ==========================================================================
// 「只有明細」= 整份文件沒有錢
// ==========================================================================
// 不是把價格欄留白。留白的欄位只會讓人問「這幾格為什麼是空的」;
// 留著小計與總計更糟 —— 那等於把最不想給的那個數字留在最顯眼的地方。
//
// 這件事在畫面上要講出來,不然沒有人知道勾下去到底會少掉什麼。

const SCOPES: { value: ExportScope; label: string; hint: string }[] = [
  { value: "all", label: "整張報價單", hint: "封面總表、明細、總計與付款條件,全部都有" },
  {
    value: "hide",
    label: "只有明細(隱藏價格)",
    hint: "項目、規格、數量都在;單價、金額、小計、總計與付款條件全部不印",
  },
];

// PDF 的兩個版本(業主 2026-09-25:「複雜版(目前這版)/標準版(新增)」)。
// 預設標準版 —— 小案子一頁印完正是業主要的;大案子再切到複雜版。
// 只有 PDF 有版本,Excel 是給人拿回去改的,沒有版面可言。
const LAYOUTS: { value: PdfLayout; label: string; hint: string }[] = [
  { value: "standard", label: "標準版(一頁)", hint: "品項全部放在一張表,小案子一頁印完" },
  { value: "full", label: "複雜版(每章一頁)", hint: "封面總表 + 每個章節各自一頁,適合大型案子" },
];

export interface ExportDialogProps {
  open: boolean;
  quoteId: number;
  quoteNo: string;
  /** `print` 時只給 PDF,而且最後那一顆鈕是「列印」。 */
  mode: "export" | "print";
  onClose: () => void;
}

/**
 * 把 PDF 送到印表機,**不另開一個分頁**。
 *
 * 做法是把它載成 blob、掛進一個看不見的 iframe 再叫 `print()`。
 * 用 `window.open` 的話會多一個分頁,而使用者印完還得自己去關掉它。
 *
 * iframe 不當場移除:移除得太早,列印對話框還沒讀完那份文件就沒了。
 * 交給列印視窗關掉之後的 `afterprint`,收不到那個事件時也有一個保底的計時器。
 */
async function printPdf(url: string): Promise<void> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`拿不到 PDF(${res.status})`);
  const blobUrl = URL.createObjectURL(await res.blob());

  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = blobUrl;

  const cleanup = () => {
    URL.revokeObjectURL(blobUrl);
    frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (win == null) {
      cleanup();
      return;
    }
    win.addEventListener("afterprint", cleanup, { once: true });
    win.focus();
    win.print();
    // 有些瀏覽器不發 afterprint(或使用者直接關掉了那個對話框)——
    // 留一個保底的收拾,不然那個 blob 與 iframe 會一直掛在頁面上。
    window.setTimeout(cleanup, 60_000);
  };

  document.body.appendChild(frame);
}

export function ExportDialog({ open, quoteId, quoteNo, mode, onClose }: ExportDialogProps) {
  const [format, setFormat] = useState<ExportFormat>("pdf");
  const [scope, setScope] = useState<ExportScope>("all");
  const [layout, setLayout] = useState<PdfLayout>("standard");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const printing = mode === "print";
  // 列印只有 PDF —— Excel 沒有「列印」這回事。
  const chosenFormat: ExportFormat = printing ? "pdf" : format;
  const url =
    `${API_BASE}/quotes/${quoteId}/export.${chosenFormat}?prices=${scope}` +
    (chosenFormat === "pdf" ? `&layout=${layout}` : "");

  const go = async () => {
    setBusy(true);
    try {
      if (printing) {
        await printPdf(url);
      } else if (typeof window !== "undefined") {
        window.open(url, "_blank", "noopener");
      }
      onClose();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "印不出來", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={printing ? `列印 ${quoteNo}` : `匯出 ${quoteNo}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            loading={busy}
            icon={
              printing ? (
                <Printer aria-hidden="true" size={16} />
              ) : (
                <Download aria-hidden="true" size={16} />
              )
            }
            onClick={() => void go()}
          >
            {printing ? "列印" : "下載"}
          </Button>
        </div>
      }
    >
      {printing ? null : (
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-sm font-medium text-[var(--text)]">檔案格式</legend>
          {(
            [
              ["pdf", "PDF", "給客戶看的那一份,版面固定"],
              ["xlsx", "Excel", "可以拿回去繼續改,金額是活公式"],
            ] as [ExportFormat, string, string][]
          ).map(([value, label, hint]) => (
            <label key={value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="export-format"
                className="mt-1"
                checked={format === value}
                onChange={() => setFormat(value)}
              />
              <span>
                <span className="font-medium text-[var(--text)]">{label}</span>
                <span className="block text-xs text-[var(--text-3)]">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {chosenFormat === "pdf" ? (
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-sm font-medium text-[var(--text)]">版本</legend>
          {LAYOUTS.map((one) => (
            <label key={one.value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="export-layout"
                className="mt-1"
                checked={layout === one.value}
                onChange={() => setLayout(one.value)}
              />
              <span>
                <span className="font-medium text-[var(--text)]">{one.label}</span>
                <span className="block text-xs text-[var(--text-3)]">{one.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1 text-sm font-medium text-[var(--text)]">要哪一份</legend>
        {SCOPES.map((one) => (
          <label key={one.value} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="export-scope"
              className="mt-1"
              checked={scope === one.value}
              onChange={() => setScope(one.value)}
            />
            <span>
              <span className="font-medium text-[var(--text)]">{one.label}</span>
              {/* 勾下去到底會少掉什麼,要講出來 —— 不然沒有人敢勾。 */}
              <span className="block text-xs text-[var(--text-3)]">{one.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </Dialog>
  );
}

/** 舊的直接下載仍然留著:寄送對話框那邊用得到。 */
export { exportUrl };
